// User profile helpers.
//
// Post-migration the Firestore `users/{uid}` doc is now a Postgres `User`
// row served by /api/auth/* and /api/users/*. The `currentUser` /
// `userProfile` surface used by every component is filled in by
// AuthContext (see /api/auth/me); this file owns the small set of
// admin-only operations that still need a direct REST round-trip.

import { apiFetch } from './_apiClient';

export const ROLES = {
  ADMIN: 'admin',
  TEACHER: 'teacher',
  PARENT: 'parent',
  STUDENT: 'student'
};

export const STAFF_ROLES = [ROLES.ADMIN, ROLES.TEACHER];

export const isStaffRole = (role) => STAFF_ROLES.includes(role);
export const isParentRole = (role) => role === ROLES.PARENT;
export const isStudentRole = (role) => role === ROLES.STUDENT;

export const resolveRole = (role) => {
  if (!role) return ROLES.TEACHER;
  if (!Object.values(ROLES).includes(role)) return ROLES.TEACHER;
  return role;
};

const safe = async (label, fn, fallback) => {
  try {
    return await fn();
  } catch (err) {
    console.warn(`userService.${label} failed:`, err.message);
    return fallback;
  }
};

export const userService = {
  async getUserProfile(uid) {
    if (!uid) return null;
    return safe('getUserProfile', async () => {
      const data = await apiFetch(`/api/users/${uid}`);
      return data?.user || null;
    }, null);
  },

  async upsertUserProfile(uid, data = {}) {
    if (!uid) throw new Error('upsertUserProfile: uid is required');
    await apiFetch(`/api/users/${uid}`, {
      method: 'PATCH',
      body: JSON.stringify(data || {})
    });
    return uid;
  },

  async createParentAccount(uid, { displayName, phone, email, linkedStudentIds = [] }) {
    return userService.upsertUserProfile(uid, {
      role: ROLES.PARENT,
      displayName: displayName || null,
      phone: phone || null,
      email: email || null,
      linkedStudentIds
    });
  },

  async setUserRole(uid, role) {
    if (!Object.values(ROLES).includes(role)) {
      throw new Error(`setUserRole: unknown role "${role}"`);
    }
    return userService.upsertUserProfile(uid, { role });
  },

  async linkStudentToParent(parentUid, studentId) {
    if (!parentUid || !studentId) return;
    await apiFetch(`/api/users/${parentUid}/linked-students/${studentId}`, {
      method: 'POST'
    });
  },

  async unlinkStudentFromParent(parentUid, studentId) {
    if (!parentUid || !studentId) return;
    await apiFetch(`/api/users/${parentUid}/linked-students/${studentId}`, {
      method: 'DELETE'
    });
  },

  async getParentsForStudent(studentId) {
    if (!studentId) return [];
    return safe('getParentsForStudent', async () => {
      const data = await apiFetch(
        `/api/users?role=parent&linkedStudentId=${encodeURIComponent(studentId)}`
      );
      return data?.users || [];
    }, []);
  }
};
